/**
 * JWT Token Storage Utility
 * Purpose: Manage JWT tokens in localStorage with security best practices
 * Author: Traklet App
 * Created: 2025
 */

// Token storage keys
const TOKEN_KEY = 'traklet_auth_token';
const USER_KEY = 'traklet_user_data';

/**
 * Token Storage Interface
 */
export interface StoredUser {
  id: number;
  email: string;
  username: string;
  role: string;
}

/**
 * JWT Token Storage Manager
 */
export class TokenStorage {
  /**
   * Store JWT token in localStorage
   */
  static setToken(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      console.log('[TokenStorage] Token stored successfully');
    } catch (error) {
      console.error('[TokenStorage] Failed to store token:', error);
    }
  }

  /**
   * Get JWT token from localStorage
   */
  static getToken(): string | null {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        console.log('[TokenStorage] Token retrieved from storage');
      }
      return token;
    } catch (error) {
      console.error('[TokenStorage] Failed to retrieve token:', error);
      return null;
    }
  }

  /**
   * Remove JWT token from localStorage
   */
  static removeToken(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      console.log('[TokenStorage] Token removed from storage');
    } catch (error) {
      console.error('[TokenStorage] Failed to remove token:', error);
    }
  }

  /**
   * Store user data in localStorage
   */
  static setUser(user: StoredUser): void {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      console.log('[TokenStorage] User data stored successfully:', user);
    } catch (error) {
      console.error('[TokenStorage] Failed to store user data:', error);
    }
  }

  /**
   * Get user data from localStorage
   */
  static getUser(): StoredUser | null {
    try {
      const userData = localStorage.getItem(USER_KEY);
      if (userData) {
        const user = JSON.parse(userData) as StoredUser;
        console.log('[TokenStorage] User data retrieved from storage:', user);
        return user;
      }
      return null;
    } catch (error) {
      console.error('[TokenStorage] Failed to retrieve user data:', error);
      return null;
    }
  }

  /**
   * Remove user data from localStorage
   */
  static removeUser(): void {
    try {
      localStorage.removeItem(USER_KEY);
      console.log('[TokenStorage] User data removed from storage');
    } catch (error) {
      console.error('[TokenStorage] Failed to remove user data:', error);
    }
  }

  /**
   * Clear all authentication data
   */
  static clearAll(): void {
    this.removeToken();
    this.removeUser();
    console.log('[TokenStorage] All authentication data cleared');
  }

  /**
   * Check if user is authenticated (has valid token)
   */
  static isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    const isAuth = !!(token && user);
    console.log('[TokenStorage] Authentication check:', { hasToken: !!token, hasUser: !!user, isAuth });
    return isAuth;
  }

  /**
   * Get authorization header for API requests
   */
  static getAuthHeader(): { Authorization: string } | {} {
    const token = this.getToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }

  /**
   * Check if token is expired (basic check)
   * Note: This is a simple check, server-side validation is still required
   */
  static isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      // Decode JWT payload (without verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp < currentTime;
      
      if (isExpired) {
        console.log('[TokenStorage] Token is expired');
        this.clearAll();
      }
      
      return isExpired;
    } catch (error) {
      console.error('[TokenStorage] Failed to decode token:', error);
      this.clearAll();
      return true;
    }
  }

  /**
   * Get token expiration time
   */
  static getTokenExpiration(): Date | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000);
    } catch (error) {
      console.error('[TokenStorage] Failed to get token expiration:', error);
      return null;
    }
  }
}

export default TokenStorage;